import CopyTextDialog from '@/components/dialog/CopyTextDialog.tsx';
import { DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu.tsx';
import { useTransientNavigate } from '@/hook/useTransientNavigate.ts';
import { createEventInGoogleCalendar } from '@/lib/calendar.ts';
import { manageError } from '@/lib/manage-error.ts';
import { ConferencePageLocationState } from '@/pages/ConferencePage.tsx';
import { authService } from '@/service/AuthService';
import { conferenceService } from '@/service/ConferenceService';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaClock, FaPlay } from 'react-icons/fa';

type CreateConferenceMenuProps = {
  onProcessing?: (processing: boolean) => void;
};

const CreateConferenceMenu: FC<CreateConferenceMenuProps> = ({ onProcessing }) => {
  const { t } = useTranslation();
  const { transientNavigate } = useTransientNavigate<ConferencePageLocationState>();

  const [isConferenceLinkDialogOpen, setIsConferenceLinkDialogOpen] = useState(false);
  const [conferenceLink, setConferenceRoomUrl] = useState<string | null>(null);

  const createConferenceRoom = useCallback(async (): Promise<string | null> => {
    onProcessing && onProcessing(true);
    try {
      if (!(await authService.isAuthorized())) {
        authService.login();
        return null;
      }

      return await conferenceService.createConferenceRoom();
    } catch (e) {
      manageError(e, t('menu.errorCreatingRoom'));
    } finally {
      onProcessing && onProcessing(false);
    }

    return null;
  }, [onProcessing, t]);

  const handleCreateConferenceForLater = useCallback(async () => {
    const roomId = await createConferenceRoom();
    if (roomId) {
      setConferenceRoomUrl(`${import.meta.env.VITE_APP_BASE_URL}/${roomId}`);
      setIsConferenceLinkDialogOpen(true);
    }
  }, [createConferenceRoom]);

  const handleCreateInstantConference = useCallback(async () => {
    onProcessing && onProcessing(true);
    try {
      const roomId = await createConferenceRoom();
      if (roomId) {
        transientNavigate(`/${roomId}`, { skipLobby: true });
      }
    } finally {
      onProcessing && onProcessing(false);
    }
  }, [createConferenceRoom, onProcessing, transientNavigate]);

  const handleCreateConferenceInGoogleCalendar = useCallback(async () => {
    onProcessing && onProcessing(true);
    try {
      const newTab = window.open(); // Open window immediately to avoid popup blocking
      const roomId = await createConferenceRoom();
      if (roomId && newTab) {
        createEventInGoogleCalendar(`${import.meta.env.VITE_APP_BASE_URL}/${roomId}`, newTab);
      }
    } finally {
      onProcessing && onProcessing(false);
    }
  }, [createConferenceRoom, onProcessing]);

  return (
    <>
      <DropdownMenuContent align="start">
        <DropdownMenuItem className="text-base p-2 text-muted-foreground" onClick={handleCreateConferenceForLater}>
          <FaClock className="inline w-4 h-4 mr-3 text-indigo-500" />
          {t('menu.createForLater')}
        </DropdownMenuItem>
        <DropdownMenuItem className="text-base p-2 text-muted-foreground" onClick={handleCreateInstantConference}>
          <FaPlay className="inline w-4 h-4 mr-3 text-indigo-500" />
          {t('menu.startInstant')}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-base p-2 text-muted-foreground"
          onClick={handleCreateConferenceInGoogleCalendar}
        >
          <FaCalendarAlt className="inline w-4 h-4 mr-3 text-indigo-500" />
          {t('menu.scheduleGoogle')}
        </DropdownMenuItem>
      </DropdownMenuContent>

      <CopyTextDialog
        open={isConferenceLinkDialogOpen}
        onOpenChange={setIsConferenceLinkDialogOpen}
        title={t('menu.joiningInfo')}
        description={t('menu.joiningDescription')}
        value={conferenceLink}
      />
    </>
  );
};

export default CreateConferenceMenu;
